import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { OBJECTIVES_EMPTY_SEARCH } from "@/router";
import { ObjectivesListShell } from "@/features/objectives/objectives-list-shell";
import { useObjectives, type ObjectiveFilters } from "@/hooks/use-objectives";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useTactics } from "@/hooks/use-tactics";
import { useCollegeGoals } from "@/hooks/use-college-goals";
import { useCollegeStrategicPillars } from "@/hooks/use-college-strategic-pillars";
import { useCurrentUser, isSupervisorOrAbove } from "@/hooks/use-current-user";
import { buildObjectiveLadder, type ObjectiveLadder } from "@/lib/objective-ladder";
import { DEFAULT_YEAR } from "@/lib/year-options";
import type { ObjectivesView } from "@/router";

export function TeamObjectivesPage() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const search = useSearch({ from: '/objectives/team' });
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const view: ObjectivesView = search.view ?? 'cards';

  // Redirect non-supervisor/non-admin users
  if (currentUser && !isSupervisorOrAbove(currentUser.role)) {
    void navigate({ to: "/objectives", search: OBJECTIVES_EMPTY_SEARCH });
    return null;
  }

  // When owner_id is set, remove team_of (they conflict — owner_id is more specific).
  // When owner_id is cleared, restore team_of.
  const baseFilters: ObjectiveFilters = currentUser && !search.owner_id
    ? { year: search.year ?? DEFAULT_YEAR, level: search.level, status: search.status, type: search.type, focus_area_id: search.focus_area_id, team_of: currentUser.id, cursor }
    : { year: search.year ?? DEFAULT_YEAR, level: search.level, status: search.status, type: search.type, focus_area_id: search.focus_area_id, owner_id: search.owner_id, cursor };
  const resolvedFilters: ObjectiveFilters = currentUser ? baseFilters : { year: DEFAULT_YEAR };

  const { data, isLoading, error } = useObjectives(resolvedFilters);
  const { data: focusAreasData } = useFocusAreas();
  const { data: tacticsData } = useTactics();
  const { data: collegeGoalsData } = useCollegeGoals();
  const { data: pillarsData } = useCollegeStrategicPillars();

  const objectives = data?.data ?? [];
  const nextCursor = data?.next_cursor ?? null;

  const ladderData = useMemo(() => ({
    focusAreas: focusAreasData?.data ?? [],
    tactics: tacticsData?.data ?? [],
    collegeGoals: collegeGoalsData?.data ?? [],
    pillars: pillarsData?.data ?? [],
  }), [focusAreasData, tacticsData, collegeGoalsData, pillarsData]);

  const ladderMap = useMemo(() => {
    const map = new Map<string, ObjectiveLadder | null>();
    for (const obj of objectives) {
      map.set(obj.id, buildObjectiveLadder(obj.focus_area_ids, obj.tactic_ids, ladderData));
    }
    return map;
  }, [objectives, ladderData]);

  const handleFilterChange = (newFilters: ObjectiveFilters) => {
    setCursor(undefined);
    void navigate({
      to: '/objectives/team',
      search: {
        year: newFilters.year ?? DEFAULT_YEAR,
        level: newFilters.level,
        status: newFilters.status,
        type: newFilters.type,
        focus_area_id: newFilters.focus_area_id,
        owner_id: newFilters.owner_id,
        view: search.view,
      },
    });
  };

  const handleViewChange = (next: ObjectivesView) => {
    void navigate({
      to: '/objectives/team',
      search: {
        ...search,
        year: search.year ?? DEFAULT_YEAR,
        view: next === 'cards' ? undefined : next,
      },
    });
  };

  return (
    <ObjectivesListShell
      headerTitle="Team Objectives"
      showSubtitle={false}
      showSummary
      filters={{
        year: search.year ?? DEFAULT_YEAR,
        level: search.level,
        status: search.status,
        type: search.type,
        focus_area_id: search.focus_area_id,
        owner_id: search.owner_id,
      }}
      onFilterChange={handleFilterChange}
      view={view}
      onViewChange={handleViewChange}
      objectives={objectives}
      ladderMap={ladderMap}
      isLoading={!currentUser || isLoading}
      error={error ? { message: error.message } : null}
      nextCursor={nextCursor}
      onLoadMore={nextCursor ? () => setCursor(nextCursor) : undefined}
      emptyTitle="No team objectives found"
      emptyDescription="Your team members' objectives will appear here. Adjust your filters if needed."
    />
  );
}
